import { actionTypes } from 'redux-resource';
import { SubmissionError } from 'redux-form';
import * as api from 'api';
import { PER_PAGE } from 'helpers/viewPurchase';
import { BIG_PAGE_SIZE } from '../constants';

export const resetCreateBuyoutPurchaseRequest = () => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'createBuyoutPurchase',
  };
  dispatch({
    ...actionDefaults,
    type: actionTypes.CREATE_RESOURCES_IDLE,
  });
};

export const createBuyoutPurchase = (attrs) => async (dispatch) => {
  const { user_profile, buyoutPurchases } = attrs;
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'createBuyoutPurchase',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.CREATE_RESOURCES_PENDING,
  });

  try {
    const purchaseResp = await api.createPurchase(user_profile);
    let purchase = purchaseResp.data;

    for (let buyoutPurchase of buyoutPurchases) {
      const { lease_buyout_id } = buyoutPurchase;
      const addItemResp = await api.addItemToPurchase(
        { item_type: 'lease_buyout', item_id: lease_buyout_id },
        purchase.id,
      );
      purchase = addItemResp.data;
    }

    const finance_partner = buyoutPurchases[0].finance_partner;
    const beginOffloadPurchaseResp = await api.beginOffloadPurchase(
      { charge_type: finance_partner },
      purchase.id,
    );

    purchase = beginOffloadPurchaseResp.data;

    dispatch({
      ...actionDefaults,
      type: actionTypes.CREATE_RESOURCES_SUCCEEDED,
      resources: [purchase],
    });
  } catch (err) {
    dispatch({
      ...actionDefaults,
      type: actionTypes.CREATE_RESOURCES_FAILED,
      requestProperties: {
        fullErrorMessage: err ? err : { detail: 'Something went wrong' },
      },
    });
  }
};

export const fetchUserPurchases =
  (userId, page = 1, params) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'purchases',
      requestKey: `fetchByUser${page}`,
      list: `userPurchases${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchUserPurchases(userId, page, params)
      .then((response) => {
        const { count, next, previous, results } = response.data;

        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results,
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
        const messages = err.response
          ? err.response.data
          : { detail: 'Something went wrong during fetching purchases' };
        throw new SubmissionError(messages);
      });
  };

export const fetchUserCanceledPurchases =
  (userId, page = 1, params) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'purchases',
      requestKey: `fetchCancelledByUser${page}`,
      list: `userCancelledPurchases${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchUserCanceledPurchases(userId, page, params)
      .then((response) => {
        const { count, next, previous } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: response.data.results,
          requestProperties: {
            perPage: response.data.results.length,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
        const messages = err.response
          ? err.response.data
          : { detail: 'Something went wrong during fetching purchases' };
        throw new SubmissionError(messages);
      });
  };

export const generateBlankInvoice = (userId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'generateBlankInvoice',
    list: 'blankInvoice',
    mergeListIds: false,
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .generateBlankInvoice(userId)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
      const messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong during fetching purchases' };
      throw new SubmissionError(messages);
    });
};

export const fetchSpecificUserPurchases =
  (userId, page = 1, params) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'purchases',
      requestKey: `fetchSpecificByUser${page}`,
      list: `specificUserPurchases${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchUserPurchases(userId, page, params)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results,
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });

        const messages = err.response
          ? err.response.data
          : { detail: 'Something went wrong during fetching purchases' };
        throw new SubmissionError(messages);
      });
  };

export const fetchUserOverduePurchases = (userId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'fetchOverdueByUser',
    list: 'userOverduePurchases',
    mergeListIds: false,
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .fetchUserOverduePurchases(userId)
    .then((response) => {
      const { count, next, previous, results } = response.data;
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: results,
        requestProperties: {
          perPage: (results && results.length) || PER_PAGE,
          count,
          next,
          previous,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
    });
};

export const findPurchase = (purchaseId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'find',
    resources: [purchaseId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .findPurchase(purchaseId)
    .then(({ data }) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [data],
      });

      return data;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
        statusCode: err.response && err.response.status,
      });
    });
};

export const fetchPurchaseLineItems =
  (purchaseId, page, filters) => (dispatch) => {
    const actionDefaults = {
      resourceType: 'purchases',
      requestKey: `fetchPurchaseLineItems${page}`,
      resources: [purchaseId],
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchPurchaseLineItems(purchaseId, page, filters)
      .then((response) => {
        const { count, next, previous, results } = response.data;

        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          meta: {
            lineItems: {
              perPage: (results && results.length) || PER_PAGE,
              count,
              next,
              previous,
              [`results${page}`]: results,
            },
          },
        });

        return results;
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
        const messages = err.response
          ? err.response.data
          : { detail: 'Something went wrong during fetching purchases' };
        throw new SubmissionError(messages);
      });
  };

export const fetchPurchaseWithAllLineItems =
  (purchaseId, filters) => async (dispatch) => {
    const actionDefaults = {
      resourceType: 'purchases',
      requestKey: `fetchPurchaseWithAllLineItems`,
      resources: [purchaseId],
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    let lineitems = [];

    let page = 1;
    let next = 'next-url';

    while (next) {
      let response;

      try {
        response = await api.fetchPurchaseWithAllLineItems(
          purchaseId,
          page,
          filters,
          BIG_PAGE_SIZE,
        );
      } catch (e) {
        let messages = e.response
          ? e.response.data
          : {
              detail:
                'Something went wrong during fetching purchase with All Line Items',
            };
        throw new SubmissionError({
          _error: messages,
        });
      }

      lineitems = lineitems.concat(response.data.results);
      next = response.data.next;
      page += 1;
    }

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_SUCCEEDED,
      meta: {
        lineItems: {
          [`results`]: lineitems,
        },
      },
    });

    return lineitems;
  };

export const createLineItem = (id, attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'createLineItem',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.CREATE_RESOURCES_PENDING,
  });

  return api
    .createLineItem(id, attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.CREATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response.data;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.CREATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const deleteLineItemsById =
  (purchaseId, linesToDelete) => (dispatch) => {
    const actionDefaults = {
      resourceType: 'purchases',
      requestKey: 'delete',
      resources: [purchaseId],
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .deleteLineItemsById(purchaseId, {
        lineitem_ids: linesToDelete.map((line) => line.id),
        no_auto_close: true,
      })
      .then(({ data }) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: [data],
        });

        return data;
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.UPDATE_RESOURCES_FAILED,
        });

        let messages = err.response
          ? err.response.data
          : { detail: 'Something went wrong' };
        if (messages.detail) {
          throw new SubmissionError({ _error: messages });
        } else if (
          messages.message ||
          messages.error ||
          messages.simo_subscription
        ) {
          throw new SubmissionError({
            _error:
              messages.message || messages.error || messages.simo_subscription,
          });
        } else {
          throw new SubmissionError(messages);
        }
      });
  };

export const partialRefund = (purchaseId, lineItemsToRefund) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'partialRefund',
    resources: [purchaseId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .partialRefund(purchaseId, lineItemsToRefund)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });

      return response;
    })
    .catch((error) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
        requestProperties: {
          fullErrorMessage: error.response && error.response.data,
        },
      });
    });
};

export const clearPartialRefundError = (purchaseId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'partialRefund',
    resources: [purchaseId],
    requestProperties: {
      fullErrorMessage: null,
    },
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
  });
};

export const getLeaseById = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'lease',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .findLease(id)
    .then(({ data }) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [data],
      });

      return data;
    })
    .catch(({ error }) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
        statusCode: error.response && error.response.status,
      });

      const messages =
        (error.response.data && `${error.response.status}`) ||
        'Something went wrong';
      throw new SubmissionError(messages);
    });
};

export const deletePurchase = (purchaseId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'deletePurchase',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.DELETE_RESOURCES_PENDING,
  });

  return api
    .deletePurchase(purchaseId)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.DELETE_RESOURCES_SUCCEEDED,
        resources: [purchaseId],
      });

      return response.data;
    })
    .catch((error) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.DELETE_RESOURCES_FAILED,
        statusCode: error && error.response && error.response.status,
      });
    });
};

export const deleteLineItemById = (purchaseId, lineItemId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'deleteLineItemById',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .deleteLineItemById(purchaseId, {
      lineitem: lineItemId,
      no_auto_close: true,
    })
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        statusCode: 200,
      });

      return response.data;
    })
    .catch((error) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
        statusCode: error && error.response && error.response.status,
      });
    });
};

export const updateOffloadAmount = (data) => (dispatch) => {
  const {
    cancelOffload,
    purchaseRemoveItem,
    purchaseAddItem,
    lease,
    purchaseId,
    leaseId,
  } = data;
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'updateOffloadAmount',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .cancelOffloadPurchase(purchaseId, cancelOffload)
    .then(() => api.deleteLineItemById(purchaseId, purchaseRemoveItem))
    .then(() => api.updateLease(leaseId, lease))
    .then(() => api.addItemToPurchase(purchaseAddItem, purchaseId))
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        statusCode: 200,
      });

      return response;
    })
    .catch((error) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
        statusCode: error && error.response && error.response.status,
      });
    });
};

export const createPaymentArrangements = (purchaseId, values) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'createPaymentArrangements',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.CREATE_RESOURCES_PENDING,
  });

  return api
    .createPaymentArrangements(purchaseId, values)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.CREATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.CREATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const upgradePurchaseAttPlan = (payload) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'upgradeAttPlan',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .upgradePurchaseAttPlan(payload)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
        requestProperties: {
          error: err.response.data,
        },
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const searchPurchases =
  (search, page = 1) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'purchases',
      requestKey: `searchPurchases${page}`,
      list: `searchPage${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    let requestPromise;
    if (search.id) {
      requestPromise = api.findPurchase(search.id).then((response) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: [response.data],
          requestProperties: {
            perPage: PER_PAGE,
            count: 1,
            next: null,
            previous: null,
          },
        });
      });
    } else {
      requestPromise = api.searchPurchases(page, search).then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results,
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      });
    }

    return requestPromise.catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
    });
  };

export const transferLineItems =
  (items, from, to, user_profile) => (dispatch) => {
    const actionDefaults = {
      resourceType: 'purchases',
      requestKey: 'transferLineItems',
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.CREATE_RESOURCES_PENDING,
    });

    return api
      .transferLineItems(items, from, to, user_profile)
      .then((response) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.CREATE_RESOURCES_SUCCEEDED,
          resources: [response.data],
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.CREATE_RESOURCES_FAILED,
        });

        let messages = err.response
          ? err.response.data
          : { detail: 'Something went wrong' };
        throw new SubmissionError(messages);
      });
  };

export const fetchPendingArrangements =
  (page = 1, search) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'purchases',
      requestKey: `pendingArrangements${page}`,
      list: `pendingArrangements${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchPendingArrangements(page, search)
      .then(async (response) => {
        const results = response.data.results;
        const { count, next, previous } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results,
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const mergePurchases = (id, attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'mergePurchases',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .mergePurchases(id, attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
        requestProperties: {
          error: err.response.data,
        },
      });

      const messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong during merge purchases' };
      throw new SubmissionError(messages);
    });
};

export const refundPurchase = (id, attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'refundPurchase',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .refundPurchase(id, attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response.data;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail) {
        throw new SubmissionError({ _error: messages });
      } else if (
        messages.message ||
        messages.error ||
        messages.simo_subscription
      ) {
        throw new SubmissionError({
          _error:
            messages.message || messages.error || messages.simo_subscription,
        });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const refundAffirm = (id, charge_id, refundType) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'refundAffirm',
  };
  const payload = {
    cancel_purchase: true,
  };
  if (refundType === 'correction_refund') {
    payload.refund_id = charge_id;
  }

  dispatch({
    ...actionDefaults,
    type: actionTypes.CREATE_RESOURCES_PENDING,
  });

  return api
    .refundAffirm(id, payload)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.CREATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });

      return response;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.CREATE_RESOURCES_FAILED,
        requestProperties: {
          error: err.response.data,
        },
      });
    });
};

export const fetchPurchaseContractByUserId =
  (userId, purchaseId) => (dispatch) => {
    const actionDefaults = {
      resourceType: 'purchases',
      requestKey: `purchaseContractByUserId${purchaseId}`,
      list: `purchaseContractByUserId${purchaseId}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchPurchaseContractByUserId(userId, purchaseId)
      .then((response) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: response,
        });

        return response;
      })
      .catch((error) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
          statusCode: error && error.response && error.response.status,
        });

        return error.response;
      });
  };

export const createPurchase = (attrs) => async (dispatch) => {
  const { user_profile } = attrs;
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'create',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.CREATE_RESOURCES_PENDING,
  });

  try {
    const purchaseResp = await api.createPurchase(user_profile);
    let purchase = purchaseResp.data;
    dispatch({
      ...actionDefaults,
      type: actionTypes.CREATE_RESOURCES_SUCCEEDED,
      resources: [purchase],
    });
    return purchase;
  } catch (err) {
    dispatch({
      ...actionDefaults,
      type: actionTypes.CREATE_RESOURCES_FAILED,
    });
    let messages = err.response
      ? err.response.data
      : { detail: 'Something went wrong' };
    throw new SubmissionError({
      _error: messages,
    });
  }
};

export const setShippingOption = (id, attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'setShippingOption',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .setShippingOption(id, attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response.data;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });
      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError({
        _error: messages,
      });
    });
};

export const recalculateShipping = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'recalculateShipping',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .recalculateShipping({}, id)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response.data;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });
      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError({
        _error: messages,
      });
    });
};

export const completeWithAch = (purchaseId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'completeWithAch',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .completeWithAch(purchaseId)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });

      return response;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const purchaseMassAction =
  (purchaseIds, status, purchases = []) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'purchases',
      requestKey: 'purchaseMassAction',
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.UPDATE_RESOURCES_PENDING,
    });

    return api
      .purchaseMassAction({
        purchase_ids: purchaseIds,
        status,
      })
      .then((response) => {
        const updatedPurchases = purchases
          .filter((purchase) => purchaseIds.includes(purchase.id))
          .map((purchase) => ({
            ...purchase,
            status,
          }));

        dispatch({
          ...actionDefaults,
          type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,

          resources: updatedPurchases,
        });

        return response;
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.UPDATE_RESOURCES_FAILED,
        });

        let messages = err.response
          ? err.response.data
          : { detail: 'Something went wrong' };
        throw new SubmissionError(messages);
      });
  };

export const generateTracfonePlanInvoice = (payload) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'generateTracfonePlanInvoice',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .generateTracfonePlanInvoice(payload)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });

      return response;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const generateTracfoneRenewalPurchases = (ids) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'generateTracfoneRenewalPurchases',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .generateTracfoneRenewalPurchases({ subscriptions: ids })
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });

      return response;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const generateSimoRenewalPurchases = (ids) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'generateSimoRenewalPurchases',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .generateSimoRenewalPurchases({ subscriptions: ids })
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });

      return response;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};
export const upgradeSimoTopUp = (payload) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'upgradeSimoTopUp',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .upgradeSimoTopUp(payload)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
        requestProperties: {
          error: err.response.data,
        },
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail) {
        throw new SubmissionError({ _error: messages });
      } else if (
        messages.message ||
        messages.error ||
        messages.simo_subscription
      ) {
        throw new SubmissionError({
          _error:
            messages.message || messages.error || messages.simo_subscription,
        });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

// export const upgradeSimoTopUp = (payload) => (dispatch) => {
//   const actionDefaults = {
//     resourceType: 'purchases',
//     requestKey: 'upgradeSimoTopUp',
//   };

//   dispatch({
//     ...actionDefaults,
//     type: actionTypes.UPDATE_RESOURCES_PENDING
//   });

//   return api.upgradeSimoTopUp(payload).then(response => {
//     dispatch({
//       ...actionDefaults,
//       type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
//       resources: [response.data]
//     });
//     return response;
//   }).catch(err => {
//     dispatch({
//       ...actionDefaults,
//       type: actionTypes.UPDATE_RESOURCES_FAILED,
//       requestProperties: {
//         error: err.response.data
//       }
//     });

//     let messages = err.response ? err.response.data : { detail: 'Something went wrong' };
//     if (messages.detail) {
//       throw new SubmissionError({ _error: messages });
//     } else if (messages.message || messages.error || messages.simo_subscription) {
//       throw new SubmissionError({ _error: messages.message || messages.error || messages.simo_subscription });
//     } else {
//       throw new SubmissionError(messages);
//     }
//   });
// }

export const upgradePurchaseSimoPlan = (payload) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'upgradeSimoPlan',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .upgradePurchaseSimoPlan(payload)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
        requestProperties: {
          error: err.response.data,
        },
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const setPurchaseDueDate = (id, attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'setPurchaseDueDate',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .setPurchaseDueDate(id, attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response.data;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });
      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail) {
        throw new SubmissionError({ _error: messages });
      } else if (
        messages.message ||
        messages.error ||
        messages.simo_subscription
      ) {
        throw new SubmissionError({
          _error:
            messages.message || messages.error || messages.simo_subscription,
        });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const setPurchaseLocked = (id, attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'setLocked',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .setPurchaseLocked(id, attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });
      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail) {
        throw new SubmissionError({ _error: messages });
      } else if (messages.message || messages.error) {
        throw new SubmissionError({
          _error: messages.message || messages.error,
        });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const allocatePurchaseLineitems = (id, attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'allocatePurchaseLineitems',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .allocatePurchaseLineitems(id, attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response.data;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail || messages.active_cycle) {
        throw new SubmissionError({ _error: messages });
      } else if (
        messages.message ||
        messages.error ||
        messages.simo_subscription
      ) {
        throw new SubmissionError({
          _error:
            messages.message || messages.error || messages.simo_subscription,
        });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const upgradePurchaseQuickPlan = (payload) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'purchases',
    requestKey: 'upgradeQuickPlan',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .upgradePurchaseQuickPlan(payload)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
        requestProperties: {
          error: err.response.data,
        },
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};
